import type { CSSProperties, PropsWithChildren, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Box, type BoxProps, Flex } from 'reflexbox';
import styled from 'styled-components';

import { ChevronRightIcon } from '../icons';
import { tokens } from '../theme/tokens';
import { IconButton } from './button/IconButton';
import { Skeleton } from './Skeleton';

const Container = styled(Box)<{
	interactive?: boolean;
	selected?: boolean;
}>`
	border-radius: ${tokens.radius.md}px;
	background: ${tokens.surface.white};
	box-shadow: ${tokens.shadow.xs};
	border: 1px solid ${tokens.border.subtle};
	box-sizing: border-box;
	overflow: hidden;

	${(props) => props.interactive && `cursor: pointer`};

	${(props) =>
		props.interactive &&
		!props.selected &&
		`
		cursor: pointer;

		&:hover {
			background: ${tokens.surface.hover};
			box-shadow: ${tokens.shadow.md};
		}

		&:active {
			background: ${tokens.surface.secondary};
		}
		`}

	${(props) => props.selected && `box-shadow: 0 0 0 2px ${tokens.text.primary};`}
`;

const Contents = styled(Box)<{ banner?: string }>`
	${(props) =>
		props.banner &&
		`
        border-left: 8px solid ${props.theme.colors[props.banner]};
        border-top-left-radius: ${tokens.radius.md}px;
        border-bottom-left-radius: ${tokens.radius.md}px;
    `}

	> [data-list-item]:last-child {
		border-bottom: 0;
	}
`;

const CardSkeletonElement = styled(Box)`
	border-radius: ${tokens.radius.md}px;
	background: ${tokens.surface.white};
`;

function CardSkeleton(
	props: BoxProps & {
		children?: ReactNode;
		/** @deprecated Compose this content in children instead. */
		subFooter?: ReactNode;
	},
) {
	const { children, subFooter, p = 3, ...rest } = props;
	return (
		<CardSkeletonElement {...rest}>
			<Box p={p}>{children}</Box>
			{subFooter}
		</CardSkeletonElement>
	);
}

const Chevron = styled.div`
	flex-grow: 0;
	flex-shrink: 0;
	pointer-events: none;
`;

type Props = PropsWithChildren<
	BoxProps & {
		link?: string;
		selected?: boolean;
		onClick?: () => void;
		/** Trailing chevron. Defaults to true when `link` is set. */
		chevron?: boolean;
		/** @deprecated Compose this content in children instead. */
		header?: ReactNode;
		/** @deprecated Compose this content in children instead. */
		footer?: ReactNode;
		/** @deprecated Compose this content in children instead. */
		subFooter?: ReactNode;
		/** @deprecated Compose a left accent in children instead. */
		banner?: string;
		/** @deprecated Pass location state through the surrounding router instead. */
		navigationState?: object;
		/** @deprecated Compose a footer chevron in children instead. */
		footerChevron?: boolean;
		style?: CSSProperties;
	}
>;

function CardBody({
	header,
	footer,
	subFooter,
	banner,
	padding,
	showChevron,
	footerChevron,
	children,
}: {
	header?: ReactNode;
	footer?: ReactNode;
	subFooter?: ReactNode;
	banner?: string;
	padding: BoxProps;
	showChevron: boolean;
	footerChevron?: boolean;
	children: ReactNode;
}) {
	return (
		<>
			{header}
			<Contents {...padding} banner={banner}>
				{showChevron ? (
					<Flex
						alignItems={footerChevron ? 'flex-end' : 'center'}
						style={{ gap: tokens.gap.group }}
					>
						<Box flexShrink={1} flexGrow={1} minWidth={0}>
							{children}
						</Box>
						<Chevron>
							<IconButton type="secondary" icon={<ChevronRightIcon />} />
						</Chevron>
					</Flex>
				) : (
					children
				)}
				{footer}
			</Contents>
			{subFooter}
		</>
	);
}

export const Card = Skeleton(function Card({
	link,
	selected,
	onClick,
	chevron,
	header,
	footer,
	subFooter,
	banner,
	navigationState,
	footerChevron,
	p = 3,
	px,
	py,
	pt,
	pb,
	pl,
	pr,
	children,
	...rest
}: Props) {
	const [pathname, params] = link?.split('?') ?? [];
	const padding = Object.fromEntries(
		Object.entries({ p, px, py, pt, pb, pl, pr }).filter(([, value]) => value !== undefined),
	);
	const showChevron = chevron ?? Boolean(link);

	const body = (
		<CardBody
			header={header}
			footer={footer}
			subFooter={subFooter}
			banner={banner}
			padding={padding}
			showChevron={showChevron}
			footerChevron={footerChevron}
		>
			{children}
		</CardBody>
	);

	if (link) {
		return (
			<Link
				to={{ pathname, search: params && `?${params}`, state: navigationState }}
				target={link.startsWith('https://') ? '_blank' : undefined}
			>
				<Container selected={selected} {...rest} interactive>
					{body}
				</Container>
			</Link>
		);
	}

	return (
		<Container
			role={onClick ? 'button' : undefined}
			selected={selected}
			interactive={!!onClick}
			onClick={onClick}
			{...rest}
		>
			{body}
		</Container>
	);
}, CardSkeleton);
